import React, { Component } from 'react';
import { Row } from 'reactstrap';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import minBy from 'lodash/minBy';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ConfirmationWell from './styled/ConfirmationWell';
import LargestCol from 'components/LargestCol';
import ChargesSummary from 'components/ChargesSummary';
import TermsAndConditionsModal from 'components/TermsAndConditionsModal';
import ButtonLink from 'components/ButtonLink';
import PrintButton from './styled/PrintButton';
import {
  PROPERTIES,
  BOOKING_ENGINE_PAGE_CHANGED,
  AAA,
  PROPERTY_TAX
} from 'utils/constants';
import {
  CANCELLATION_POLICY,
  PROPERTY_TERMS_AND_CONDITIONS_FULL
} from 'utils/messages/termsAndConditions';
import { gaModalView } from 'utils/googleAnalytics';

class BookingConfirmation extends Component {
  state = {
    termsAndConditionsModalShown: false
  };

  componentDidMount() {
    const parentWindow = window.parent;

    parentWindow.postMessage(BOOKING_ENGINE_PAGE_CHANGED, '*');
  }

  toggleTermsAndConditionsModal = () => {
    if (!this.state.termsAndConditionsModalShown) {
      // Record modal view in GA.
      gaModalView('terms-and-conditions');
    }

    this.setState({
      termsAndConditionsModalShown: !this.state.termsAndConditionsModalShown
    });
  };

  render() {
    const {
      hotelReservation,
      guestInfo,
      selectedRoom,
      property,
      printItinerary,
      numberOfGuests,
      reviewAndSubmitForm,
      isWaitListBooking
    } = this.props;
    const { termsAndConditionsModalShown } = this.state;

    const cardNumber = get(guestInfo, 'cardNumber', '');

    // Find the lowest roomRate.
    const minRoomRate = minBy(
      selectedRoom.roomRates,
      roomRate => roomRate.total.amount
    );
    const taxesAndFees = minRoomRate.expectedCharges.totalTaxesAndFees;
    const total = minRoomRate.total.amount;

    const subTotal = Math.max(0, total - taxesAndFees);
    const discounts =
      (reviewAndSubmitForm && reviewAndSubmitForm.discounts) || [];
    const tripleADiscount = discounts.find(discount => discount === AAA);
    const discountedSubTotal = tripleADiscount ? subTotal * 0.9 : subTotal;
    const discountedTax = discountedSubTotal * PROPERTY_TAX[property];

    return (
      <ConfirmationWell className="booking-confirmation printable">
        <h1 className="sr-only">
          Your Wind Creek {PROPERTIES[property]} reservation is confirmed!
        </h1>
        <h2 className="print-header">
          Your Wind Creek {PROPERTIES[property]} reservation is confirmed!
        </h2>
        <h4>
          BOOKING CONFIRMATION
          <PrintButton onClick={printItinerary}>PRINT</PrintButton>
        </h4>
        <h5>
          CONFIRMATION NUMBER:{' '}
          <span>
            {isWaitListBooking
              ? 'Waitlist Request Submitted'
              : hotelReservation.id}
          </span>
        </h5>
        {guestInfo && (
          <React.Fragment>
            <Row>
              <LargestCol xs="12" sm="6">
                <p>
                  {guestInfo.firstName} {guestInfo.lastName}
                </p>
                <p>{guestInfo.email}</p>
                <p>{guestInfo.phone}</p>
              </LargestCol>
              <LargestCol xs="12" sm="6">
                <p>{guestInfo.address}</p>
                <p>
                  {guestInfo.city}&nbsp;
                  {guestInfo.state.value}&nbsp;
                  {guestInfo.postalCode}
                </p>
              </LargestCol>
            </Row>
          </React.Fragment>
        )}
        {hotelReservation && (
          <div className="itinerary-details">
            <h4>BOOKING DETAILS</h4>
            {hotelReservation.roomStays.map((roomStay, i) => {
              const startDateMoment = moment(roomStay.startDate);
              const endDateMoment = moment(roomStay.endDate);

              const startFormatted = startDateMoment.format('MMM DD, YYYY');
              const endFormatted = endDateMoment.format('MMM DD, YYYY');
              const days = endDateMoment.diff(startDateMoment, 'days');

              return (
                <Row key={i}>
                  <LargestCol xs="12" sm="8">
                    <p>
                      <span className="property">{property}</span>
                      &bull;
                      <span>{startFormatted}</span>
                      <FontAwesomeIcon
                        icon="long-arrow-alt-right"
                        aria-label="to"
                      />
                      <span>{endFormatted}</span>
                    </p>
                    <p>
                      <span>
                        {selectedRoom &&
                          selectedRoom.roomType.roomTypeDescription}
                      </span>
                    </p>
                    {guestInfo.cardNumber && (
                      <p>
                        {get(guestInfo, 'cardType.label', '')} XXXXXXXXXXXX
                        {cardNumber.substr(-4)}
                      </p>
                    )}
                  </LargestCol>
                  <LargestCol xs="12" sm="4">
                    <p>
                      <span>
                        {days + 1} Days, {days} {days > 1 ? 'Nights' : 'Night'}
                      </span>
                    </p>
                    {numberOfGuests && (
                      <span>
                        {numberOfGuests.value +
                          ' Guest' +
                          (numberOfGuests.value > 1 ? 's' : '')}
                      </span>
                    )}
                  </LargestCol>
                </Row>
              );
            })}
          </div>
        )}
        <ChargesSummary
          room={selectedRoom}
          total={discountedSubTotal + discountedTax}
          taxesAndFees={discountedTax}
          confirmation
        />
        <h5>PLEASE NOTE</h5>
        <p>
          Hotel stay is valid for Account Holder only. Offer is
          non-transferable. Valid ID required. A copy of this confirmation has
          been sent to your email. Upon check-in, we will authorize your credit
          card for room and tax plus an additional $50 to cover incidental
          charges. A credit will be issued at check-out for any unused credit
          being held on your account.
        </p>
        <p>{CANCELLATION_POLICY(property)}</p>
        {PROPERTY_TERMS_AND_CONDITIONS_FULL[property] && (
          <p>
            <ButtonLink onClick={this.toggleTermsAndConditionsModal}>
              See full terms and conditions
            </ButtonLink>
          </p>
        )}
        <TermsAndConditionsModal
          property={property}
          isOpen={termsAndConditionsModalShown}
          toggle={this.toggleTermsAndConditionsModal}
        />
      </ConfirmationWell>
    );
  }
}

BookingConfirmation.propTypes = {
  hotelReservation: PropTypes.shape({
    id: PropTypes.string,
    reservationId: PropTypes.string,
    roomStays: PropTypes.arrayOf(
      PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        total: PropTypes.shape({
          amount: PropTypes.number,
          currencyCode: PropTypes.number,
          decimals: PropTypes.number
        }),
        expectedCharges: PropTypes.shape({
          totalRoomAndRatePackages: PropTypes.number,
          totalTaxesAndFees: PropTypes.number,
          taxInclusive: PropTypes.number,
          decimals: PropTypes.number
        })
      })
    )
  }),
  guestInfo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.number,
    cvv: PropTypes.number
  }),
  selectedRoom: PropTypes.object,
  property: PropTypes.string,
  printItinerary: PropTypes.func,
  numberOfGuests: PropTypes.any,
  reviewAndSubmitForm: PropTypes.shape({
    alerts: PropTypes.arrayOf(PropTypes.string),
    roomFeatures: PropTypes.arrayOf(PropTypes.string),
    traces: PropTypes.arrayOf(PropTypes.string),
    discounts: PropTypes.arrayOf(PropTypes.string)
  }),
  isWaitListBooking: PropTypes.bool
};

export default BookingConfirmation;
